'use client';

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Search, HelpCircle, Mail, Phone, ExternalLink, Home, Users, AlertCircle, Shield } from 'lucide-react';
import Link from 'next/link';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AnimatedSection,
  AnimatedCard,
  AnimatedIcon,
  AnimatedFeature,
  staggerContainer,
  fadeInUp
} from '@/components/AnimatedComponents';

// FAQ categories and questions
const faqData = [
  {
    category: 'general',
    title: 'General Questions',
    icon: HelpCircle,
    questions: [
      {
        question: 'What is RentWise?',
        answer: 'RentWise is a platform that helps tenants make informed rental decisions by providing transparent reviews and information about rental properties. We aim to create a more transparent rental market where tenants have access to reliable information about potential homes before signing a lease.'
      },
      {
        question: 'How does RentWise work?',
        answer: 'RentWise collects verified reviews from current and past tenants about their rental experiences. These reviews cover various aspects such as property condition, landlord responsiveness, neighborhood safety, and more. This information is then made available to prospective tenants searching for their next home.'
      },
      {
        question: 'Is RentWise available in my area?',
        answer: 'RentWise is currently available in major cities across Nigeria. We\'re rapidly expanding our coverage to more locations. Check our property listings to see if your area is covered, or contact us to request coverage for your location.'
      },
      {
        question: 'Is RentWise free to use?',
        answer: 'Basic property information and limited reviews are available for free. For comprehensive access to all reviews and detailed property insights, we offer affordable subscription plans. Visit our Pricing page for more details.'
      },
      {
        question: 'How accurate are the reviews on RentWise?',
        answer: 'We take the accuracy of our reviews very seriously. All reviews go through a verification process to ensure they come from actual tenants. We use various methods including proof of residency checks and AI-powered fraud detection to maintain the integrity of our platform.'
      }
    ]
  },
  {
    category: 'tenants',
    title: 'For Tenants',
    icon: Users,
    questions: [
      {
        question: 'How do I search for properties on RentWise?',
        answer: 'You can search for properties by location, property type, price range, and other filters. Simply go to the Browse page, enter your desired location, and use the available filters to narrow down your search.'
      },
      {
        question: 'How do I read reviews for a property?',
        answer: 'Once you find a property you\'re interested in, click on it to view its details page. There, you\'ll find all available reviews from past and current tenants, as well as an overall rating for the property.'
      },
      {
        question: 'What if the property I\'m interested in has no reviews?',
        answer: 'Not all properties have reviews yet, especially in newer areas where we\'re still growing. You can still check nearby properties for insights about the neighborhood. You can also be the first to leave a review after your tenancy!'
      },
      {
        question: 'How do I leave a review for my current or past rental?',
        answer: 'To leave a review, create an account or log in, then navigate to "Leave a Review" from your dashboard. You\'ll need to verify your tenancy by providing some documentation like a lease agreement or utility bill.'
      },
      {
        question: 'Are my personal details safe when I leave a review?',
        answer: 'Absolutely. You can choose to leave reviews anonymously, and we never disclose your personal information to landlords or other users. We only use your verification documents to confirm you were an actual tenant.'
      }
    ]
  },
  {
    category: 'landlords',
    title: 'For Landlords',
    icon: Home,
    questions: [
      {
        question: 'How do I claim my property on RentWise?',
        answer: 'To claim your property, navigate to the property page and click the "Claim This Property" button. You\'ll need to verify ownership through our verification process, which may include providing property documents and identification.'
      },
      {
        question: 'What benefits do landlords get from RentWise?',
        answer: 'As a verified landlord on RentWise, you can respond to tenant reviews, showcase property improvements, list available units, and build a positive online reputation. Our platform helps you attract quality tenants who value transparency.'
      },
      {
        question: 'Can I remove negative reviews about my property?',
        answer: 'Reviews that meet our community guidelines cannot be removed simply for being negative. However, you can respond to reviews to provide your perspective, and we remove reviews that violate our policies or are proven false.'
      },
      {
        question: 'How much does it cost for landlords to use RentWise?',
        answer: 'Basic property claiming is free. We offer premium landlord packages that include enhanced property listings, priority placement in search results, and detailed analytics. Visit our Pricing page for more information.'
      },
      {
        question: 'How can I improve my property\'s rating?',
        answer: 'The best way to improve your rating is to address tenant concerns promptly, maintain your property well, and provide good service. You can also respond professionally to reviews and document improvements you\'ve made to the property.'
      }
    ]
  },
  {
    category: 'account',
    title: 'Account & Privacy',
    icon: Shield,
    questions: [
      {
        question: 'How do I create an account?',
        answer: 'You can create an account by clicking the "Sign Up" button in the top right corner of the website. You\'ll need to provide some basic information and verify your email address to get started.'
      },
      {
        question: 'Can I use RentWise without creating an account?',
        answer: 'Yes, you can browse properties and see basic information without an account. However, to access full reviews, leave your own reviews, or use advanced features, you\'ll need to create an account.'
      },
      {
        question: 'How does RentWise protect my privacy?',
        answer: 'We take data privacy seriously and comply with relevant data protection regulations. We use industry-standard security measures to protect your information, and we never share your personal details with third parties without your consent. For more details, please see our Privacy Policy.'
      },
      {
        question: 'How do I delete my account?',
        answer: 'You can request account deletion from your account settings page. Please note that while your personal information will be removed, any reviews you\'ve posted may remain on the platform (anonymized) unless you specifically request their removal.'
      },
      {
        question: 'What happens to my data if I delete my account?',
        answer: 'When you delete your account, your personal identification information is removed from our systems. Anonymous review data may be retained for statistical purposes. For complete data removal, you can make a specific request through our contact page.'
      }
    ]
  },
  {
    category: 'troubleshooting',
    title: 'Troubleshooting',
    icon: AlertCircle,
    questions: [
      {
        question: 'I found incorrect information about a property. How do I report it?',
        answer: 'If you notice incorrect property information, you can report it by clicking the "Report Issue" button on the property page. Our team will review and verify the correction needed.'
      },
      {
        question: 'Why was my review rejected?',
        answer: 'Reviews may be rejected if they violate our community guidelines, contain offensive language, make unverifiable claims, or if we couldn\'t verify your tenancy. Check your email for specific feedback about your review.'
      },
      {
        question: 'I forgot my password. How do I reset it?',
        answer: 'You can reset your password by clicking "Forgot Password" on the login page. We\'ll send a password reset link to your registered email address.'
      },
      {
        question: 'The property I lived in isn\'t listed on RentWise. What should I do?',
        answer: 'You can add a new property by navigating to "Leave a Review" and then selecting "Property not listed?" Our team will verify the property\'s existence before publishing.'
      },
      {
        question: 'The website isn\'t working properly. What should I do?',
        answer: 'Try clearing your browser cache and cookies, or try a different browser. If problems persist, please contact our support team with details about the issue, including screenshots if possible.'
      }
    ]
  }
];

export default function FAQPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('general');
  const [expandedQuestions, setExpandedQuestions] = useState<{[key: string]: boolean}>({});

  // Filter questions based on search query
  const filteredFAQs = searchQuery 
    ? faqData.flatMap(category => 
        category.questions
          .filter(q => 
            q.question.toLowerCase().includes(searchQuery.toLowerCase()) || 
            q.answer.toLowerCase().includes(searchQuery.toLowerCase())
          )
          .map(q => ({ ...q, category: category.category }))
      )
    : [];

  // Toggle question expansion
  const toggleQuestion = (categoryId: string, questionIndex: number) => {
    const key = `${categoryId}-${questionIndex}`;
    setExpandedQuestions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <main className="flex-1 max-w-5xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <AnimatedSection
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl mx-auto text-center mb-10 sm:mb-14"
        >
          <h1 className="text-2xl sm:text-4xl font-bold mb-4 text-gray-900 bg-clip-text text-transparent bg-linear-to-r from-blue-700 to-blue-800">
            Frequently Asked Questions
          </h1>
          <p className="text-base sm:text-lg text-gray-700 mb-6">
            Find answers to common questions about using RentWise. Can't find what you're looking for? Contact our support team.
          </p>
          
          {/* Search Bar */}
          <div className="relative max-w-xl mx-auto mb-8 px-0">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <Input 
              type="text"
              placeholder="Search questions..." 
              className="pl-10 py-4 text-base rounded-full border-gray-200 shadow-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          {/* Search Results */}
          {searchQuery && (
            <AnimatedSection
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-8 sm:mb-10 text-left px-1 sm:px-0"
            >
              <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">
                Search Results {filteredFAQs.length > 0 ? `(${filteredFAQs.length})` : ''}
              </h2>
              {filteredFAQs.length > 0 ? (
                <div className="space-y-3 sm:space-y-4">
                  {filteredFAQs.map((faq, idx) => (
                    <AnimatedCard key={idx} className="bg-white rounded-lg shadow-md border border-gray-100 overflow-hidden">
                      <button
                        onClick={() => toggleQuestion('search', idx)}
                        className="w-full flex justify-between items-center text-left p-4 hover:bg-gray-50"
                      >
                        <h3 className="font-medium text-gray-900 text-sm sm:text-base pr-4">{faq.question}</h3>
                        {expandedQuestions[`search-${idx}`] ? (
                          <ChevronUp className="h-5 w-5 text-gray-500 shrink-0" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-gray-500 shrink-0" />
                        )}
                      </button>
                      {expandedQuestions[`search-${idx}`] && (
                        <div className="p-4 pt-0 bg-white animate-slideDown">
                          <p className="text-gray-700 text-sm sm:text-base">{faq.answer}</p>
                          <div className="mt-2 text-xs sm:text-sm">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                              {faqData.find(cat => cat.category === faq.category)?.title}
                            </span>
                          </div>
                        </div>
                      )}
                    </AnimatedCard>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 sm:py-8">
                  <p className="text-gray-500">No results found. Try a different search term or browse our categories below.</p>
                </div>
              )}
            </AnimatedSection>
          )}
        </AnimatedSection>
        
        {/* FAQ Categories and Questions */}
        {!searchQuery && (
          <>
            {/* Mobile: Dropdown for categories */}
            <div className="block sm:hidden mb-6">
              <select
                className="w-full rounded-lg border border-gray-200 p-3 text-base font-medium focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={activeCategory}
                onChange={e => setActiveCategory(e.target.value)}
              >
                {faqData.map((category) => (
                  <option key={category.category} value={category.category}>
                    {category.title}
                  </option>
                ))}
              </select>
            </div>
            <Tabs defaultValue="general" value={activeCategory} onValueChange={setActiveCategory} className="w-full">
              {/* Desktop/Tablet: Tabs */}
              <TabsList className="hidden sm:flex gap-2 mb-8 bg-transparent">
                {faqData.map((category) => (
                  <TabsTrigger 
                    key={category.category} 
                    value={category.category}
                    className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700 data-[state=active]:shadow-md px-4 py-2 whitespace-nowrap text-base font-medium"
                  >
                    <div className="flex items-center gap-2">
                      <category.icon className="h-5 w-5" />
                      <span>{category.title}</span>
                    </div>
                  </TabsTrigger>
                ))}
              </TabsList>
              {faqData.map((category) => (
                <TabsContent key={category.category} value={category.category} forceMount={true}>
                  {activeCategory === category.category && (
                    <AnimatedSection
                      variants={staggerContainer}
                      initial="hidden"
                      animate="visible"
                      className="space-y-5"
                    >
                      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">{category.title} FAQs</h2>
                      {category.questions.map((faq, index) => (
                        <AnimatedCard 
                          key={index}
                          variants={fadeInUp} 
                          className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden"
                        >
                          <button
                            onClick={() => toggleQuestion(category.category, index)}
                            className="w-full flex justify-between items-center text-left p-4 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-200"
                          >
                            <h3 className="font-medium text-gray-900 text-base pr-4">{faq.question}</h3>
                            {expandedQuestions[`${category.category}-${index}`] ? (
                              <ChevronUp className="h-5 w-5 text-gray-500 shrink-0" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-gray-500 shrink-0" />
                            )}
                          </button>
                          {expandedQuestions[`${category.category}-${index}`] && (
                            <div className="px-4 pb-4 animate-slideDown">
                              <p className="text-gray-700 text-base">{faq.answer}</p>
                            </div>
                          )}
                        </AnimatedCard>
                      ))}
                    </AnimatedSection>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          </>
        )}
        
        {/* Contact Section */}
        <AnimatedSection
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="mt-16 sm:mt-20 bg-white rounded-xl shadow-lg border border-blue-100/50 p-5 sm:p-8"
        >
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">Still have questions?</h2>
            <p className="text-gray-600 mt-2">Our support team is here to help you with any questions or concerns</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <AnimatedCard className="flex flex-col items-center p-4 sm:p-6 text-center bg-blue-50 rounded-lg">
              <AnimatedIcon index={0}>
                <Mail className="h-8 sm:h-10 w-8 sm:w-10 text-blue-600 mb-2 sm:mb-3" />
              </AnimatedIcon>
              <h3 className="font-medium text-base sm:text-lg mb-1 sm:mb-2">Email Support</h3>
              <p className="text-gray-600 mb-3 sm:mb-4 text-base sm:text-base">Send us a message anytime</p>
              <a href="mailto:support@tryrentwise.com" className="text-blue-600 font-medium text-base sm:text-base">support@tryrentwise.com</a>
            </AnimatedCard>
            
            <AnimatedCard className="flex flex-col items-center p-4 sm:p-6 text-center bg-blue-50 rounded-lg">
              <AnimatedIcon index={1}>
                <Phone className="h-8 sm:h-10 w-8 sm:w-10 text-blue-600 mb-2 sm:mb-3" />
              </AnimatedIcon>
              <h3 className="font-medium text-base sm:text-lg mb-1 sm:mb-2">Phone Support</h3>
              <p className="text-gray-600 mb-3 sm:mb-4 text-base sm:text-base">Mon-Fri, 9am-6pm WAT</p>
              <p className="text-blue-600 font-medium text-base sm:text-base">+234 800 000 0000</p>
            </AnimatedCard>
          </div>
          
          <div className="mt-6 sm:mt-8 text-center">
            <Link href="/contact">
              <Button className="bg-[var(--color-brand-primary)] hover:bg-[var(--color-brand-primary-hover)] text-white px-4 sm:px-6 py-2.5 sm:py-5 text-base sm:text-lg rounded-full font-medium">
                Visit Contact Page
                <ExternalLink className="ml-2 h-4 sm:h-5 w-4 sm:w-5" />
              </Button>
            </Link>
          </div>
        </AnimatedSection>
      </main>
  );
} 